import { ItemBillingFlag } from "@app/shared/filters/item-billing-flag";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz/esm";

export type ReportsShowFilters = { [key: string]: boolean };

/** Subset of AuditFilterData used by project-hours filtering. */
export type ProjectHoursAuditFilter = {
	duration: boolean;
	mod: number;
	modTime: string;
	project: boolean;
	task: boolean;
	timezone: "keep" | "adjust";
};

export type FilterableProjectHour = {
	billable?: boolean;
	projectIsDefault?: boolean;
	taskId?: string | null;
	duration?: number | null;
	start: string | number | Date;
	end?: string | number | Date | null;
	timeZone?: string;
	currentUserCanEdit?: boolean;
	projectArchived?: boolean;
	amount?: number | null;
	viewStart?: string;
	viewEnd?: string;
	editable?: boolean;
};

/** Parse "HH:mm" audit modTime into absolute seconds. */
export function parseAuditModTimeSeconds(modTime: string): number {
	const [hours, minutes] = modTime.split(":", 2).map(Number);
	const seconds = (hours || 0) * 3600 + (minutes || 0) * 60;
	return seconds < 0 ? -seconds : seconds;
}

function adjustZonedViewTimes<T extends FilterableProjectHour>(
	items: T[],
	myTimezone: string
): Array<T & { viewStart: string; viewEnd: string }> {
	const parseZonedTime = (dt: Date, fromTz?: string) =>
		utcToZonedTime(
			zonedTimeToUtc(dt, fromTz ?? Intl.DateTimeFormat().resolvedOptions().timeZone).getTime(),
			myTimezone
		);
	return items.map((x) => {
		const start = parseZonedTime(new Date(x.start), x.timeZone).toISOString();
		const end = parseZonedTime(new Date(x.end ?? x.start), x.timeZone).toISOString();
		return { ...x, viewStart: start, viewEnd: end };
	});
}

/**
 * Single-purpose project-hours client filter used by reports data$filtered.
 * Does not mutate the input array.
 */
export function filterProjectHoursForReports<T extends FilterableProjectHour>(
	data: T[],
	billable: ItemBillingFlag,
	audit: ProjectHoursAuditFilter | null | undefined,
	show: ReportsShowFilters,
	options?: { viewerTimezone?: string | null }
): T[] {
	let next: T[] = data;
	let copied = false;
	const ensureCopy = () => {
		if (!copied) {
			next = [...next];
			copied = true;
		}
	};

	if (show.billable && billable !== ItemBillingFlag.BOTH) {
		ensureCopy();
		const wantBilled = billable === ItemBillingFlag.BILLED;
		next = next.filter((x) => (wantBilled ? x.billable : !x.billable));
	}

	if (show.audit && audit) {
		if (audit.project) {
			ensureCopy();
			next = next.filter((x) => x.projectIsDefault);
		}
		if (audit.task) {
			ensureCopy();
			next = next.filter((x) => !x.taskId);
		}
		if (audit.duration && audit.modTime) {
			ensureCopy();
			const isLess = audit.mod === 0;
			const threshold = parseAuditModTimeSeconds(audit.modTime);
			next = next.filter((x) => (isLess ? threshold <= (x.duration ?? 0) : threshold >= (x.duration ?? 0)));
		}
		const myTimezone = options?.viewerTimezone;
		if (audit.timezone === "adjust" && myTimezone) {
			next = adjustZonedViewTimes(next, myTimezone);
			copied = true;
		}
	}

	return next;
}

/** Attach editable flag used by detailed/summary tables. Always returns a new array. */
export function withProjectHourEditableFlags<T extends FilterableProjectHour>(
	items: T[]
): Array<T & { editable: boolean }> {
	return items.map((item) => ({
		...item,
		editable: item.projectArchived ? false : !!item.currentUserCanEdit,
	}));
}

export type ProjectHourDurationStats = {
	summed: number;
	billed: number;
	nonBilled: number;
	amount: { value: number } | null;
};

/** One-pass duration / amount totals for filtered project hours. */
export function sumProjectHourDurationStats(
	items: Array<Pick<FilterableProjectHour, "duration" | "billable" | "amount">>
): ProjectHourDurationStats {
	let summed = 0;
	let billed = 0;
	let nonBilled = 0;
	let amountSum = 0;
	let hasAmount = false;
	for (const item of items) {
		const duration = ~~item.duration;
		summed += duration;
		if (item.billable) billed += duration;
		else nonBilled += duration;
		if (item.amount !== undefined && item.amount !== null) {
			hasAmount = true;
			amountSum += item.amount;
		}
	}
	return {
		summed,
		billed,
		nonBilled,
		amount: hasAmount ? { value: amountSum } : null,
	};
}

/** Unique user ids across comego + project time arrays. */
export function uniqueUserIdsFromReportItems(items: Array<{ userId: string }>): string[] {
	const ids = new Set<string>();
	for (const item of items) {
		if (item.userId) ids.add(item.userId);
	}
	return Array.from(ids);
}
